import { Link } from 'react-router-dom';
import { Building2, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { StageBadge } from './StageBadge';
import { CLIENT_TYPE_LABELS, type Client } from '@/types/clients';
import { formatCurrency } from '@/lib/utils';

type Props = {
  clients: Client[];
  onEdit: (client: Client) => void;
  onDelete: (client: Client) => void;
  canWrite: boolean;
};

export function ClientsTableView({ clients, onEdit, onDelete, canWrite }: Props) {
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  return (
    <Card className="p-0 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="kicker px-6 py-3 font-normal">Client</th>
            <th className="kicker px-6 py-3 font-normal">Type</th>
            <th className="kicker px-6 py-3 font-normal">Stage</th>
            <th className="kicker px-6 py-3 font-normal">Territory</th>
            <th className="kicker px-6 py-3 font-normal text-right">Annual Value</th>
            <th className="kicker px-6 py-3 font-normal w-12" />
          </tr>
        </thead>
        <tbody>
          {clients.map((c) => (
            <tr key={c.id} className="border-b border-border last:border-0 hover:bg-bg-hover/40 transition-colors group">
              <td className="px-6 py-4">
                <Link to={`/clients/${c.id}`} className="flex items-center gap-3 min-w-0">
                  <div className="h-8 w-8 shrink-0 rounded-sm bg-walnut-700/60 flex items-center justify-center">
                    <Building2 size={13} className="text-brass-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-text truncate group-hover:text-brass-300 transition-colors">{c.name}</div>
                    {c.email && <div className="text-xs text-text-dim truncate">{c.email}</div>}
                  </div>
                </Link>
              </td>
              <td className="px-6 py-4">
                <Badge tone="neutral">{CLIENT_TYPE_LABELS[c.type]}</Badge>
              </td>
              <td className="px-6 py-4">
                <StageBadge stage={c.stage} />
              </td>
              <td className="px-6 py-4 text-text-muted">
                {c.territory || <span className="text-text-dim">—</span>}
              </td>
              <td className="px-6 py-4 text-right tabular text-text">
                {c.estimatedAnnualValue
                  ? formatCurrency(c.estimatedAnnualValue)
                  : <span className="text-text-dim">—</span>}
              </td>
              <td className="px-6 py-4 text-right relative">
                {canWrite && (
                  <>
                    <button
                      onClick={(e) => { e.preventDefault(); setOpenMenu(openMenu === c.id ? null : c.id); }}
                      className="p-1.5 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm transition-colors opacity-0 group-hover:opacity-100"
                      aria-label="Actions"
                    >
                      <MoreVertical size={15} />
                    </button>
                    {openMenu === c.id && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                        <div className="absolute right-4 top-12 z-20 w-40 bg-bg-raised border border-border-strong rounded-sm shadow-raised py-1 animate-fade-in">
                          <button
                            onClick={() => { onEdit(c); setOpenMenu(null); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left"
                          >
                            <Pencil size={13} /> Edit
                          </button>
                          <div className="my-1 border-t border-border" />
                          <button
                            onClick={() => { onDelete(c); setOpenMenu(null); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left"
                          >
                            <Trash2 size={13} /> Delete
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
